import {CopyButton, IconButton} from '@wandb/components';
import {Alert} from '@wandb/weave/components/Alert';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useLocalStorage} from '@wandb/weave/util/useLocalStorage';
import {last} from 'lodash';
import React, {useCallback, useRef, useState} from 'react';
import {Virtuoso, VirtuosoHandle} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {
  COMPACT_LINE_HEIGHT,
  DEFAULT_LINE_HEIGHT,
  OUTER_PADDING,
  TRUNCATED_LABEL_MAX_CHARS,
} from './constants';
import {useLogLabels} from './hooks/useLogLabels';
import {useLogSearch} from './hooks/useLogSearch';
import {useLogsFormattedForCopy} from './hooks/useLogsFormattedForCopy';
import {useVisibleLogs} from './hooks/useVisibleLogs';
import {type LogLabelFilterOption} from './LogLabelFilter';
import {LogLabelFilter} from './LogLabelFilter';
import {type LogLineData, LogLineRenderer} from './LogLineRenderer';
import {LogSearchBrowser} from './LogSearchBrowser';
import {TimestampVisibilitySelect} from './TimestampVisibilitySelect';

export interface LogViewerProps {
  loading: boolean;
  lines: LogLineData[];
  outputLogFile: string | null;
  totalLines?: number;
  onTimestampVisibilityChange?: (isVisible: boolean) => void;
  onLabelFiltersApplied?: (
    selectedCount: number,
    isSearchActive: boolean
  ) => void;
  onLogFileDownload?: () => void;
  renderLabelsBanner?: () => React.ReactNode;
  renderLimitReachedBanner?: (numLinesAvailable: number) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
}

type NumberedLogLine = LogLineData & {lineNum?: number};

export const edgesToNumberedLines = (
  edges: Array<{node: LogLineData}>,
  totalLines?: number
): NumberedLogLine[] => {
  if (!totalLines) {
    return edges.map(({node}) => node);
  }

  return edges.map(({node}, idx) => {
    return {
      ...node,
      lineNum: idx + 1 + Math.max(totalLines - edges.length, 0),
    };
  });
};

/**
 * Core log viewer implementation that is shared between app and weave frontends.
 */
export const LogViewer: React.FC<LogViewerProps> = ({
  loading,
  lines,
  totalLines,
  outputLogFile,
  onTimestampVisibilityChange,
  onLabelFiltersApplied,
  onLogFileDownload,
  renderLabelsBanner,
  renderLimitReachedBanner,
  renderLoading,
  renderEmpty,
}) => {
  const [isTimestampVisible, setIsTimestampVisible] = useLocalStorage(
    'logTimestampsVisible',
    true
  );

  const [selectedLabels, setSelectedLabels] = useState<
    readonly LogLabelFilterOption[]
  >([]);
  // Keep track of selections when the label filter dropdown opens
  const [selectedLabelsOnMenuOpen, setSelectedLabelsOnMenuOpen] = useState<
    readonly LogLabelFilterOption[] | null
  >(null);

  const {all: allLines, filtered: filteredLines} = useVisibleLogs(
    lines,
    selectedLabels,
    loading
  );

  const allLabels = useLogLabels(allLines);
  const filteredLabels = selectedLabels.length ? selectedLabels : allLabels;

  const logsFormattedForCopy = useLogsFormattedForCopy(
    filteredLines,
    filteredLabels,
    isTimestampVisible
  );

  const lineHeight =
    isTimestampVisible || filteredLabels.length > 0
      ? DEFAULT_LINE_HEIGHT
      : COMPACT_LINE_HEIGHT;

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // used to determine list height based on where it is in the viewport
  const [listContainerTop, setListContainerTop] = useState<number | undefined>(
    undefined
  );

  const scrollTo = useCallback(
    (row: number) => {
      virtuosoRef.current?.scrollToIndex({
        index: row,
        align: 'center',
        behavior: 'auto',
      });
    },
    [virtuosoRef]
  );

  const {
    searchText,
    searchActive,
    searchMatches,
    onSearchChange,
    onSetIndex,
    onKeyDown,
  } = useLogSearch(filteredLines, scrollTo);

  // Find longest visible lineNum & label to help us align log contents
  const maxLineNumLength = last(filteredLines)?.lineNum?.toString().length ?? 0;
  const maxLabelLength = Math.min(
    Math.max(...filteredLabels.map(({displayText}) => displayText.length), 0),
    TRUNCATED_LABEL_MAX_CHARS + 1 // +1 for ellipsis character
  );

  const itemContent = useCallback(
    (index: number) => {
      const line = filteredLines[index];

      return (
        <LogLineRenderer
          line={line}
          index={index}
          showTimestamp={isTimestampVisible}
          showLabel={filteredLabels.length > 0}
          showLineNumber={line.lineNum != null}
          maxLineNumLength={maxLineNumLength}
          maxLabelLength={filteredLabels.length > 0 ? maxLabelLength : 0}
          searchText={searchText}
          searchMatches={searchMatches}
          searchActive={searchActive}
          className="px-16 py-2"
        />
      );
    },
    [
      filteredLabels,
      filteredLines,
      isTimestampVisible,
      maxLabelLength,
      maxLineNumLength,
      searchActive,
      searchMatches,
      searchText,
    ]
  );

  if (loading) {
    if (renderLoading) {
      return <>{renderLoading()}</>;
    }
    return <Tailwind>Loading logs…</Tailwind>;
  }

  if (allLines.length === 0) {
    if (renderEmpty) {
      return <>{renderEmpty()}</>;
    }
    return (
      <div style={{margin: 10}}>
        <Alert severity="info">No log output available.</Alert>
      </div>
    );
  }

  const isStorageLimitReached = totalLines && totalLines > allLines.length;

  return (
    <Tailwind>
      <div className={`bg-white p-${OUTER_PADDING}`}>
        {isStorageLimitReached && renderLimitReachedBanner?.(allLines.length)}
        {allLabels.length > 0 && renderLabelsBanner?.()}
        <div className="flex flex-wrap gap-16 md:flex-nowrap">
          <div className="mt-4 flex w-full flex-wrap gap-8 md:flex-nowrap">
            <div className="w-full md:w-auto">
              <TimestampVisibilitySelect
                isTimestampVisible={isTimestampVisible}
                onChange={value => {
                  setIsTimestampVisible(value);
                  onTimestampVisibilityChange?.(value);
                }}
              />
            </div>
            {allLabels.length > 0 && (
              <div className="w-full min-w-[200px] flex-1 md:w-[60%] md:max-w-[650px]">
                <LogLabelFilter
                  allLabels={allLabels}
                  selectedLabels={selectedLabels}
                  onChange={setSelectedLabels}
                  onMenuOpen={() => {
                    setSelectedLabelsOnMenuOpen(selectedLabels);
                  }}
                  onMenuClose={() => {
                    if (selectedLabels !== selectedLabelsOnMenuOpen) {
                      onLabelFiltersApplied?.(
                        selectedLabels.length,
                        !!searchText
                      );
                    }
                    setSelectedLabelsOnMenuOpen(null);
                  }}
                />
              </div>
            )}
            <div className="flex w-full gap-8 md:w-[40%]">
              <div className="flex-1 md:max-w-[300px]">
                <TextField
                  ariaLabel="Search logs"
                  icon="search"
                  placeholder="Search"
                  value={searchText}
                  onChange={onSearchChange}
                  onKeyDown={onKeyDown}
                />
              </div>
              {searchText && (
                <LogSearchBrowser
                  index={searchActive}
                  count={searchMatches.ordered.length}
                  onSetIndex={onSetIndex}
                />
              )}
            </div>
          </div>
          <div className="flex w-full gap-8 md:w-auto">
            <CopyButton content={logsFormattedForCopy} size="md" />
            {outputLogFile && (
              <IconButton
                icon="download"
                variant="secondary"
                size="md"
                tooltip={{children: 'Download log file'}}
                aria-label="Download log file"
                href={outputLogFile}
                download="output.log"
                onClick={onLogFileDownload}
              />
            )}
          </div>
        </div>
        <div
          ref={node => {
            if (node) {
              setListContainerTop(node.getBoundingClientRect().top);
            }
          }}
          style={{
            height: listContainerTop
              ? `calc(100vh - ${listContainerTop + OUTER_PADDING}px)`
              : 0,
            lineHeight: `${lineHeight}px`,
          }}
          className={twMerge(
            'mt-16 bg-moon-50 py-16',
            'font-mono text-[13px]'
          )}>
          <Virtuoso
            computeItemKey={index => filteredLines[index]?.id ?? index}
            data={filteredLines}
            defaultItemHeight={lineHeight}
            initialTopMostItemIndex={Math.max(filteredLines.length - 1, 0)}
            itemContent={itemContent}
            // Render additional logs to improve scroll performance for large
            // lists. Note that this value is arbitrary, but mostly eliminates
            // flickering when scrolling rapidly.
            increaseViewportBy={lineHeight * 100}
            ref={virtuosoRef}
            // Prevent flickering when scrolling backwards; for context, see
            // https://github.com/petyosi/react-virtuoso/issues/1096#issuecomment-2262255320.
            skipAnimationFrameInResizeObserver={true}
            style={{height: '100%'}}
          />
        </div>
      </div>
    </Tailwind>
  );
};
